import PropTypes from 'prop-types';
import React, { memo, useCallback, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import style from './style.module.css';

/**
 * Text clamped to a number of lines with "Show more" toggle when it overflows.
 *
 * @param props.children {String} Text content.
 * @param props.lines {Number} Visible line count while collapsed.
 */
const ExpandableTextComponent = ({ children, lines = 5, className }) => {
  const textRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    if (isExpanded) return undefined;

    const node = textRef.current;

    const handleResize = () => {
      setIsOverflowing(node.scrollHeight > node.clientHeight + 1);
    };

    handleResize();

    const observer = new ResizeObserver(handleResize);

    observer.observe(node);

    return () => observer.disconnect();
  }, [children, lines, isExpanded]);

  const handleToggle = useCallback(() => {
    setIsExpanded(previous => !previous);
  }, []);

  return (
    <span className={classNames(style.host, className)}>
      <span
        ref={textRef}
        className={classNames(style.text, { [style.clamped]: !isExpanded })}
        style={isExpanded ? undefined : { WebkitLineClamp: lines }}
      >
        {children}
      </span>
      {(isOverflowing || isExpanded) && (
        <button aria-expanded={isExpanded} className={style.toggle} onClick={handleToggle} type="button">
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </span>
  );
};

ExpandableTextComponent.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  lines: PropTypes.number,
};

export const ExpandableText = memo(ExpandableTextComponent);
